import * as base64 from 'base-64'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Button from '@/modules/shared/components/button/Button'
import { RowsPerPageOptions } from '@/modules/shared/components/table/RowsPerPageOptions'
import DownloadAsCSVModal from '@/modules/shared/components/table/download-as-csv-modal/DownloadAsCSVModal'
import useGlobalManageColumns from '@/modules/shared/components/table/hooks/useGlobalManageColumns'
import ManageTablePreferencesModal from '@/modules/shared/components/table/manage-table-preferences/ManageTablePreferencesModal'
import { ColumnDefinition, PaginationDefinition, SortOption } from '@/modules/shared/components/table/types'
import { DEFAULT_ROWS_PER_PAGE } from '@/modules/shared/constants'
import { ChevronLeftIcon } from '@/modules/shared/icons/ChevronLeftIcon'
import { ChevronRightIcon } from '@/modules/shared/icons/ChevronRightIcon'
import { DownloadIcon } from '@/modules/shared/icons/DownloadIcon'
import { SettingsIcon } from '@/modules/shared/icons/SettingsIcon'
import { classNames } from '@/modules/shared/utils/classNames'

interface TableControlProps<T> {
  /** pagination enabled with settings */
  pagination?: PaginationDefinition

  /** manage columns enabled */
  manageColumns?: boolean

  /** download as csv enabled */
  downloadAsCsv?: boolean

  /** global manage columns enabled*/
  globalManageColumns?: boolean

  /** ensure that the column preference modal does not overlap.
   * Note: use this prop only if we want to manage columns of more than one table on the same page.
   * For example: view cart summary page has multiple identical tables and we want to manage it all at once.
   */
  columnPreferenceModalOverlapping?: boolean

  /** the column visibility settings */
  filteredColumns?: ColumnDefinition<T>[]

  /** set the column preferences */
  onPreferencesChange?: (filteredColumns: ColumnDefinition<T>[], sortOption: SortOption) => void

  /** Updating preferences */
  updatingPreferences?: boolean

  /** Default columns are used to restore default preferences. */
  defaultColumns?: ColumnDefinition<T>[]

  // Sort options
  sortOptions?: SortOption[]

  // sort option has been saved to the server
  savedSortPreferences?: SortOption

  /** indicates if it is the top control */
  top?: boolean

  /** Data that will be mapped in the table, used for page scrolling in control component */
  dataSource?: T[]

  /** table name */
  tableName?: string

  /** function fires when click on the rows per page button */
  onRowsPerPageChange?: (rowsPerPage: number) => void

  /** rows per page */
  rowsPerPage?: number
}

export default function TableControl<T>({
  pagination,
  manageColumns,
  downloadAsCsv,
  filteredColumns,
  onPreferencesChange,
  globalManageColumns,
  columnPreferenceModalOverlapping,
  updatingPreferences,
  defaultColumns,
  sortOptions,
  savedSortPreferences,
  top,
  dataSource,
  tableName,
  rowsPerPage,
  onRowsPerPageChange,
}: TableControlProps<T>) {
  const { t } = useTranslation()
  const [showManageColumns, setShowManageColumns] = useState(false)
  const [showDownloadAsCsv, setShowDownloadAsCsv] = useState(false)
  const [initialLoad, setInitialLoad] = useState(true)
  const { isGlobalManageColumns, closeGlobalManageColumns } = useGlobalManageColumns()
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (isGlobalManageColumns && globalManageColumns) setShowManageColumns(true)
  }, [isGlobalManageColumns])

  // Pagination settings
  const { hasNextPage, hasPreviousPage, onNextPage, onPreviousPage, loading, endCursor } = pagination || {}

  // Calculates the current page based on the endCursor and the rowsPerPage
  const calculateCurrentPage = (endCursor: string | null | undefined, itemsPerPage: number): number => {
    // if no endCursor then we are on the first page
    if (!endCursor) return 1
    const endCursorDecoded = base64.decode(endCursor) // converts the endCursor to a number string
    const endCursorInt = parseInt(endCursorDecoded, 10) // converts the endCursor to an integer
    const currentPage = Math.floor(endCursorInt / itemsPerPage) // calculates the current page from the rows

    // if the remainder of endCursorInt / itemsPerPage is greater than 0 then currenPage +1 otherwise we are on the last pages
    return endCursorInt % itemsPerPage > 0 ? currentPage + 1 : currentPage
  }

  const currentPage = calculateCurrentPage(endCursor, rowsPerPage || DEFAULT_ROWS_PER_PAGE)

  useEffect(() => {
    if (top) {
      if (initialLoad) {
        setInitialLoad(false)
      } else if (!hasNextPage && dataSource && dataSource?.length > 0) {
        // Only scroll up if there are results and on the last page, stops scrolling when there are no more results
        scrollRef?.current?.scrollIntoView({ block: 'start', behavior: 'smooth' })
      }
    }
  }, [hasNextPage])

  return (
    <>
      {(pagination || manageColumns) && (
        <div className="mb-5 flex items-center justify-between" ref={scrollRef}>
          {!!pagination && (
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <RowsPerPageOptions
                  rowsPerPage={rowsPerPage || DEFAULT_ROWS_PER_PAGE}
                  onRowsPerPageChange={onRowsPerPageChange || (() => {})}
                />
                <div className="ml-3 text-sm">
                  {hasNextPage || loading
                    ? t('requisition.details.pageNumberOnly', 'Page {{ pageNumber }}', {
                        pageNumber: currentPage,
                      })
                    : t('requisition.details.pageNumberLast', 'Page {{ pageNumber }} of {{ pageNumber }}', {
                        pageNumber: currentPage,
                      })}
                </div>
              </div>
            </div>
          )}
          <div
            className={classNames('flex items-center gap-x-2', {
              'w-full justify-end': !pagination && manageColumns,
            })}
          >
            {downloadAsCsv && (
              <Button
                className="flex items-center gap-x-2 rounded-md bg-gray-200 px-4 py-2 hover:brightness-95"
                onClick={() => setShowDownloadAsCsv(true)}
              >
                <span className="text-sm">{t('general.downloadAsCSV', 'Download as CSV')}</span>
                <DownloadIcon className="size-6" />
              </Button>
            )}
            {manageColumns && (
              <Button
                data-testid="manage-cols-btn"
                className="flex items-center gap-x-2 rounded-md bg-gray-200 px-4 py-2 hover:brightness-95"
                onClick={() => setShowManageColumns(true)}
              >
                <span className="text-sm">{t('general.manageColumns', 'Manage Columns')}</span>
                <SettingsIcon className="size-6" />
              </Button>
            )}

            {!!pagination && (
              <>
                <Button
                  type="button"
                  data-testid="previous-page-btn"
                  disabled={!hasPreviousPage}
                  loading={loading}
                  className="rounded-md bg-gray-200 px-3 py-2"
                  onClick={onPreviousPage}
                >
                  <ChevronLeftIcon className="size-6" />
                </Button>
                <Button
                  type="button"
                  data-testid="next-page-btn"
                  disabled={!hasNextPage}
                  loading={loading}
                  className="rounded-md bg-gray-200 px-3 py-2"
                  onClick={onNextPage}
                >
                  <ChevronRightIcon className="size-6" />
                </Button>
              </>
            )}
          </div>
        </div>
      )}

      {(manageColumns || globalManageColumns) && filteredColumns && onPreferencesChange && (
        <ManageTablePreferencesModal
          defaultColumns={defaultColumns}
          showModal={showManageColumns && !!columnPreferenceModalOverlapping}
          setShowModal={() => {
            setShowManageColumns(false)
            closeGlobalManageColumns()
          }}
          filteredColumns={filteredColumns}
          onPreferencesChange={onPreferencesChange}
          updatingPreferences={!!updatingPreferences}
          sortOptions={sortOptions}
          savedSortPreferences={savedSortPreferences as SortOption}
        />
      )}
      {showDownloadAsCsv && filteredColumns && dataSource && (
        <DownloadAsCSVModal
          showModal={showDownloadAsCsv}
          setShowModal={setShowDownloadAsCsv}
          columns={filteredColumns}
          dataSource={dataSource}
          tableName={tableName}
        />
      )}
    </>
  )
}
